/**
 * CONSAI — Mobile Bottom Navigation (dark, feature-colored active glow)
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import {
  LayoutDashboard, ShoppingCart, Users, User, BarChart3, Menu,
  Package, Receipt, BookOpen, FileText, Brain, Truck
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { getFeatureColor } from '../lib/feature-colors';

const MobileNav = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const primaryNavItems = [
    { id: 'dashboard', label: language === 'hi' ? 'होम' : 'Home',     icon: LayoutDashboard, path: '/dashboard', feature: 'neutral' },
    { id: 'sales',     label: language === 'hi' ? 'बिक्री' : 'Sales', icon: ShoppingCart,    path: '/sales',     feature: 'sales' },
    { id: 'stock',     label: language === 'hi' ? 'स्टॉक' : 'Stock', icon: Package,         path: '/stock',     feature: 'stock' },
    { id: 'invoices',  label: language === 'hi' ? 'इनवॉइस' : 'Invoices', icon: Receipt,     path: '/invoices',  feature: 'invoice' },
  ];

  const secondaryNavItems = [
    { id: 'customers',  label: language === 'hi' ? 'ग्राहक' : 'Customers', icon: Users, path: '/customers', feature: 'customer' },
    { id: 'suppliers',  label: language === 'hi' ? 'सप्लायर' : 'Suppliers', icon: Truck, path: '/suppliers', feature: 'supplier' },
    { id: 'analytics',  label: language === 'hi' ? 'AI एनालिटिक्स' : 'AI Analytics', icon: BarChart3, path: '/analytics', feature: 'analytics' },
    { id: 'credit',     label: language === 'hi' ? 'उधार/खाताबुक' : 'Credit/Khatabook', icon: BookOpen, path: '/credit', feature: 'credit' },
    { id: 'report',     label: language === 'hi' ? 'AI बिज़नेस रिपोर्ट' : 'AI Business Report', icon: FileText, path: '/report', feature: 'ai' },
    { id: 'ai-assistant', label: language === 'hi' ? 'AI सहायक' : 'AI Assistant', icon: Brain, path: '/ai-assistant', feature: 'ai' },
    { id: 'profile',    label: language === 'hi' ? 'प्रोफाइल' : 'Profile', icon: User, path: '/profile', feature: 'neutral' },
  ];

  const handleNavClick = (item) => { navigate(item.path); setMoreMenuOpen(false); };
  const isSecondaryActive = secondaryNavItems.some(item => location.pathname === item.path);

  const renderTab = (item, active) => {
    const fc = getFeatureColor(item.feature);
    return (
      <button
        key={item.id}
        onClick={() => handleNavClick(item)}
        className="relative flex flex-col items-center justify-center w-full h-full transition-all"
        style={{ color: active ? '#FFFFFF' : '#71717A' }}
        data-testid={`mobile-nav-${item.id}`}
      >
        <item.icon className="h-5 w-5" style={{ color: active ? fc.hex : '#71717A' }} />
        <span className={`text-[11px] mt-1 ${active ? 'font-medium' : ''}`}>{item.label}</span>
        {active && (
          <div
            className="absolute top-0 w-12 h-[2px] rounded-b-full"
            style={{ background: fc.hex, boxShadow: `0 0 10px ${fc.hex}` }}
          />
        )}
      </button>
    );
  };

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 transform-gpu"
      style={{
        background: 'rgba(10,10,11,0.92)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        position: 'fixed',
      }}
    >
      <div className="flex items-center justify-around h-16 safe-area-pb">
        {primaryNavItems.map((item) => renderTab(item, location.pathname === item.path))}

        <Sheet open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
          <SheetTrigger asChild>
            <button
              className="relative flex flex-col items-center justify-center w-full h-full transition-all"
              style={{ color: isSecondaryActive ? '#FFFFFF' : '#71717A' }}
              data-testid="mobile-nav-more"
            >
              <Menu className="h-5 w-5" style={{ color: isSecondaryActive ? '#A78BFA' : '#71717A' }} />
              <span className={`text-[11px] mt-1 ${isSecondaryActive ? 'font-medium' : ''}`}>
                {language === 'hi' ? 'और' : 'More'}
              </span>
              {isSecondaryActive && (
                <div
                  className="absolute top-0 w-12 h-[2px] rounded-b-full"
                  style={{ background: '#A78BFA', boxShadow: '0 0 10px #A78BFA' }}
                />
              )}
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="h-auto max-h-[70vh] rounded-t-3xl border-t border-white/10"
            style={{ background: '#0E0E10', color: '#FFFFFF' }}
          >
            <div className="grid grid-cols-3 gap-3 py-4">
              {secondaryNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                const fc = getFeatureColor(item.feature);
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item)}
                    className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all"
                    style={{
                      background: isActive ? fc.tint : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${isActive ? fc.border : 'rgba(255,255,255,0.06)'}`,
                      color: isActive ? '#FFFFFF' : '#A1A1AA',
                    }}
                    data-testid={`mobile-nav-more-${item.id}`}
                  >
                    <item.icon className="h-6 w-6" style={{ color: isActive ? fc.hex : '#71717A' }} />
                    <span className="text-xs text-center leading-tight">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};

export default MobileNav;
